import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, ControlLabel, FormControl } from 'react-bootstrap';
import PropTypes from 'prop-types';
import AlertComponent from '../../../../../../components/Alert';
import { MODAL_EDIT_SUBCATEGORY } from '../../../constants/modals';
import { delay, getErrorMessage } from '../../../../../../utils/helper';
import Loader from '../../../../../../components/Loaders/Loader';
import ModalPreloader from '../../../../../../components/Modal/Preloader';
import { ticketFormCategory, ticketFormSubcategoryAdd, ticketFormSubcategoryEdit } from '../../../../../../services/endpoints/settings';

const EditSubcategoryModal = ({ data, modalKey, onHide, onSubmit }) => {
  const [form, setForm] = useState({
    isLoadingPage: true,
    isLoading: false,
    init: false,
    error: null,
    success: false,
    languages: [],
    name: {},
  })
  const { isLoadingPage, isLoading, init, error, success, name, languages } = form;

  const isEdit = modalKey === MODAL_EDIT_SUBCATEGORY;

  const defaultLanguageCode = useMemo(() => {
    const defaultLanguage = languages.find((language) => !!language.language_code);
    return defaultLanguage?.language_code;
  }, [languages]);

  const fetchForm = async () => {
    try {
      const query = {};
      if (isEdit) {
        query.itemId = data.id
      }
      const response = await ticketFormCategory(query);
      setForm((prevState) => ({
        ...prevState,
        init: true,
        isLoadingPage: false,
        languages: response.data,
        name: response.data.reduce((accumulator, currentValue) => {
          accumulator[currentValue.language_code] = currentValue.value || '';
          return accumulator;
        }, {}),
      }))
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setForm((prevState) => ({
        ...prevState,
        isLoadingPage: false,
        error: errorMessage,
      }))
    }
  }

  useEffect(() => {
    fetchForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitForm = async (e) => {
    e.preventDefault();

    try {
      setForm((prevState) => ({
        ...prevState,
        error: null,
        isLoading: true,
      }));

      isEdit ? await ticketFormSubcategoryEdit(data.id, { name }) : await ticketFormSubcategoryAdd(data.id, { name });

      await setForm((prevState) => ({
        ...prevState,
        success: true,
      }));
      await delay(500);

      // fetch
      onSubmit()
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setForm((prevState) => ({
        ...prevState,
        isLoading: false,
        error: errorMessage,
      }));
    }
  };

  const onChangeName = (e) => {
    const value = e.target.value;

    setForm((prevState) => ({
      ...prevState,
      name: {
        ...prevState.name,
        [defaultLanguageCode]: value,
      },
    }))
  }

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit subcategory' : 'Add subcategory'}</Modal.Title>
      </Modal.Header>
      <ModalPreloader
        isLoading={isLoadingPage}
      >
        <form onSubmit={onSubmitForm}>
          <Modal.Body>
            {isLoading && <Loader success={success} />}
            {error && <AlertComponent text={error} type="danger" />}
            {init
              ? (
                <>
                  <ControlLabel>Subcategory name</ControlLabel>
                  <FormControl
                    onChange={onChangeName}
                    value={name[defaultLanguageCode]}
                    required
                    autoFocus
                    type="text"
                    disabled={isLoading}
                  />
                </>
              ) : null}
          </Modal.Body>
          <Modal.Footer className="custom__modal-footer">
            {init
              ? (
                <Button
                  disabled={isLoading}
                  bsStyle="primary"
                  type="submit"
                >
                  <span>{isEdit ? 'Save changes' : 'Add subcategory'}</span>
                </Button>
              ) : null}
            <Button
              disabled={isLoading}
              onClick={onHide}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </form>
      </ModalPreloader>
    </>
  );
};

EditSubcategoryModal.propTypes = {
  modalKey: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onHide: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default EditSubcategoryModal;
